import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function TraffordCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Trafford Pothole Claims | RED Best Practice Rating | Fixtyer"
        description="Trafford Council earns GREEN on spend but RED on DfT best practice — with just 24.5% preventative maintenance in 2024/25, 15% of 656km U-roads in RED condition, and a three-year survey cycle. See what the AMBER rating means for your claim."
        canonical="/councils/trafford"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Trafford', url: '/councils/trafford' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Trafford spends more than its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spending. Trafford\'s overall rating is AMBER because condition is AMBER and best practice is RED: preventative maintenance was just 24.49% of capital spend in 2024/25, B and C roads in RED rose from 4% to 6%, and 15% of unclassified roads remain in RED condition.',
          },
          {
            question: 'What does Trafford\'s RED best practice rating mean for my claim?',
            answer: 'The DfT best practice scorecard and Trafford\'s published spending figures are contextual information about network-level maintenance. They do not establish liability, notice of a specific defect, or whether the inspection and repair response for your road was reasonable.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 656.195km — 79% of Trafford\'s 826km network. The council surveys unclassified roads on a three-year cycle across North, Central and South areas because "the cost & time required to survey the entire network is prohibitive." At the 2024 survey, 15% of U-roads were in RED condition — roughly 98km of residential streets. If your road was not in the surveyed third that year, network-level condition data may not cover it.',
          },
          {
            question: 'Trafford filled fewer than 3,000 potholes in 2024/25 — does that weaken my claim?',
            answer: 'Repair totals are network-level contextual information and do not determine an individual claim. Trafford filled 2,959 potholes in 2024/25 and expects "in excess of 2500 pothole repairs" in 2025/26, while B and C roads in RED rose from 4% in 2020 to 6% in 2024 and U-road RED condition has sat at 14–16% since surveys resumed. The records for the specific defect remain central.',
          },
          {
            question: 'Were U-road condition surveys conducted during COVID?',
            answer: 'No. Trafford\'s transparency report states that no unclassified road surveys were conducted during COVID restriction years in 2020 and 2021. For incidents on residential roads in those years, there is no published network-level U-road condition data at all — on the road class that makes up 79% of the borough\'s 826km network.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Trafford Council"
        councilSlug="trafford"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/trafford"
        headline="Trafford Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Trafford</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Trafford: 24.5% Preventative Spend, RED Best Practice
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Trafford Council outspends its DfT capital allocation and earns a GREEN spend scorecard.
            Yet the DfT rates its best practice <strong className="text-rose-400">RED</strong> — because
            Trafford&apos;s own transparency report shows just <strong className="text-rose-400">24.5%
            preventative maintenance</strong> in 2024/25, <strong className="text-amber-400">15% of
            656km unclassified roads in RED condition</strong> on a three-year survey cycle, and
            <strong className="text-rose-400"> 12,841 pothole repairs in five years</strong>.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">24.5%</div>
            <div className="text-white font-medium mt-2">Preventative capital spend in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Trafford&apos;s own figure — against 38.8% reactive maintenance. The DfT best practice
              scorecard is RED for a reason: this is a predominantly patch-and-fill programme.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* RED Best Practice */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            The DfT&apos;s best practice scorecard is Trafford&apos;s weakest rating — and the council&apos;s
            own spending table explains why
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative*</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive**</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£2.32m</td>
                  <td className="px-4 py-3 text-center">£4.23m</td>
                  <td className="px-4 py-3 text-center">£2.44m</td>
                  <td className="px-4 py-3 text-center">18.68%</td>
                  <td className="px-4 py-3 text-center">36.58%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2.91m</td>
                  <td className="px-4 py-3 text-center">£4.21m</td>
                  <td className="px-4 py-3 text-center">£2.50m</td>
                  <td className="px-4 py-3 text-center">15.22%</td>
                  <td className="px-4 py-3 text-center">37.22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£2.91m</td>
                  <td className="px-4 py-3 text-center">£8.24m</td>
                  <td className="px-4 py-3 text-center">£2.58m</td>
                  <td className="px-4 py-3 text-center">36.42%</td>
                  <td className="px-4 py-3 text-center">23.84%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£3.28m</td>
                  <td className="px-4 py-3 text-center">£7.54m</td>
                  <td className="px-4 py-3 text-center">£2.78m</td>
                  <td className="px-4 py-3 text-center">39.13%</td>
                  <td className="px-4 py-3 text-center">26.91%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center">£2.91m</td>
                  <td className="px-4 py-3 text-center">£4.59m</td>
                  <td className="px-4 py-3 text-center">£2.91m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">24.49%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">38.75%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£2.91m</td>
                  <td className="px-4 py-3 text-center font-bold">£3.79m</td>
                  <td className="px-4 py-3 text-center">£3.00m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">31.65%</td>
                  <td className="px-4 py-3 text-center font-bold">44.81%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-8">
            * Percentage of overall capital expenditure on highway preventative maintenance as defined by the DfT.
            ** Percentage spent on reactive maintenance as a figure of total capital and revenue spending combined.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The Reactive Tilt</h3>
              <p className="text-rose-800 text-sm">
                Preventative spend collapsed from 39.13% in 2023/24 to 24.49% in 2024/25 — while reactive
                maintenance rose to 38.75%. The 2025/26 projection shows reactive at 44.81% and preventative
                still under one-third. That is exactly the patch-first pattern the DfT&apos;s best practice
                scorecard penalises.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">GREEN Spend, Different Story</h3>
              <p className="text-emerald-800 text-sm">
                Capital spend exceeded the DfT allocation in every published year — peaking at £8.24m in
                2022/23 against a £2.91m allocation. The council is not underfunded in aggregate. The
                question is how the money is deployed — and the report&apos;s own split answers that.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="As part of our reactive repair maintenance spending, we expect there to be in excess of 2500 pothole repairs during this financial year."
            source="Trafford Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Trafford&apos;s own transparency report
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">The network (826.067km total roads)</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">75.3km</div>
                <div className="text-sm text-surface-600">A roads (9.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">94.6km</div>
                <div className="text-sm text-surface-600">B and C roads (11.4%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">656.2km</div>
                <div className="text-sm text-rose-800">Unclassified (79.4%)</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Four-fifths of Trafford&apos;s carriageway network is unclassified — dense urban residential
              and estate roads where most pothole damage claims originate.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (75.3km): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.3%</td>
                    <td className="px-4 py-2 text-center">26.2%</td>
                    <td className="px-4 py-2 text-center">69.5%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.4%</td>
                    <td className="px-4 py-2 text-center">24.3%</td>
                    <td className="px-4 py-2 text-center">72.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6.7%</td>
                    <td className="px-4 py-2 text-center">25.3%</td>
                    <td className="px-4 py-2 text-center">68.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.8%</td>
                    <td className="px-4 py-2 text-center">24.3%</td>
                    <td className="px-4 py-2 text-center">70.9%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">5.9%</td>
                    <td className="px-4 py-2 text-center font-bold">26.7%</td>
                    <td className="px-4 py-2 text-center font-bold">67.3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads on the Greater Manchester Key Route Network are surveyed annually in both directions
              via SCANNER. Green condition has slipped from 72.3% to 67.3% since 2021.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (94.6km): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">27.8%</td>
                    <td className="px-4 py-2 text-center">68.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21.5%</td>
                    <td className="px-4 py-2 text-center">75.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">23.7%</td>
                    <td className="px-4 py-2 text-center">71.3%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">27%</td>
                    <td className="px-4 py-2 text-center font-bold">67%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads are up 50% since 2020 (4% → 6%), and good-condition roads have
              fallen from 68.2% to 67%. A third of the B/C network is now amber or red.
            </p>
          </div>

          <QuoteCard
            quote="Over the last 5 years, we have managed to maintain A Roads, which are primarily part of the Greater Manchester Key Route Network, in a good condition, keeping the lengths of road in each condition band similar over that same period."
            source="Trafford Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* U-road blind spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 656km Three-Year Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            79% of the network surveyed on a three-year cycle — with no amber category at all
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not measured — COVID</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not measured — COVID</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The unclassified road network makes up over 650km (400 miles), making up over 79% of the borough's road network."
              source="Trafford Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The cost & time required to survey the entire network is prohibitive, therefore we survey these roads over a 3 year cycle, within the areas listed below."
              source="Trafford Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">North, Central, South</h3>
              <p className="text-surface-600 text-sm mb-3">
                Trafford divides its U-road network into three areas and surveys one third each year via
                Coarse Visual Inspections (CVI). Unlike SCANNER on classified roads, CVI surveys produce
                only two categories — red and green — with no amber band for roads approaching failure.
              </p>
              <p className="text-surface-600 text-sm">
                At 15% RED in 2024, roughly <strong>98km of residential roads</strong> should be
                considered for maintenance — on a network where your street may not have been
                condition-surveyed for up to two years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The COVID Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                No unclassified road surveys were conducted in 2020 or 2021. For incidents in those
                years, there is <strong>no network-level U-road condition data at all</strong> — on the
                road class that makes up 79% of the borough.
              </p>
              <p className="text-amber-800 text-sm">
                The council identifies roads near the &ldquo;tipping point&rdquo; into RED locally, but
                that is a discretionary overlay — not the published network survey your claim can cite.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Trafford must show it had a reasonable system for
              knowing the condition of its roads. For the unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Which third of the borough was surveyed in the year of your incident — North, Central or South?</li>
              <li>• If 15% of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• How does a three-year CVI cycle catch defects that form between surveys?</li>
              <li>• Why is preventative spend under 25% while U-road RED condition holds at 14–16%?</li>
            </ul>
            <p className="text-white font-medium">
              The RED best-practice rating and three-year condition-survey cycle are relevant questions
              to explore, but neither proves liability nor automatically defeats a Section 58 defence.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            12,841 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Actual repair counts from Trafford&apos;s transparency report — a reactive maintenance programme
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (actual)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,059</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,855</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022 to 2024*</td>
                  <td className="px-6 py-4 text-center">2,471</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,497</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,959</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12,841</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-8">
            * Year label reproduced exactly as published in the council&apos;s transparency report (likely
            intended as 2022/23).
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~8 Potholes a Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Trafford fills around eight potholes per day. The council
                expects over 2,500 reactive repairs in 2025/26 alone — while planning just 2km of
                resurfacing and 3½km of surface treatment. Patching outpaces prevention.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Since April 2020</h3>
              <p className="text-amber-800 text-sm">
                The report states Trafford has resurfaced or reconstructed over 20km of roads and given
                100km surface dressing — alongside 12,841 pothole fills. Reactive repairs and
                preventative treatments are running in parallel, not replacing each other.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Trafford Checks Its Roads
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER on classified roads, CVI on residential streets — and a national standard change coming
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">SCANNER (A, B and C roads)</h3>
              <p className="text-surface-600 text-sm mb-3">
                Vehicle-mounted digital surveys cover the entire A, B and C network in both directions
                each year. Defects are categorised into red, amber and green bands. A-roads are primarily
                on the Greater Manchester Key Route Network.
              </p>
              <p className="text-surface-600 text-sm">
                B/C roads in RED rose from 4% to 6% over five years despite annual SCANNER coverage —
                evidence that surveying alone does not prevent deterioration when preventative spend
                is under 25%.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">CVI (unclassified roads)</h3>
              <p className="text-amber-800 text-sm mb-3">
                Two-person survey vans carry out Coarse Visual Inspections on one third of U-roads
                per year across North, Central and South zones. CVI produces only red and green
                categories — no amber for roads approaching failure.
              </p>
              <p className="text-amber-800 text-sm">
                Footways are surveyed separately via Footway Network Surveys on a five-year cycle across
                five borough areas — a further blind spot for pavement claims.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-surface-200 mb-8">
            <h3 className="font-semibold text-brand-900 mb-3">PAS 2161 From 2026/27</h3>
            <p className="text-surface-600 text-sm">
              From 2026 to 2027, local highway authorities must use suppliers accredited against PAS 2161,
              expanding condition categories from three to five. Until then, the RED/amber/green data in
              Trafford&apos;s published report — and the two-tier CVI system on U-roads — is what governs
              your claim.
            </p>
          </div>

          <QuoteCard
            quote="A robust asset management approach ensures we direct limited funding to where it is needed most, making sure we achieve maximum effectiveness in maintaining the A roads."
            source="Trafford Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend, RED-Practice Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Trafford invests well — but invests reactively
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital exceeds DfT allocation in every published year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition broadly stable over five years of annual SCANNER surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published HIAMP framework — 20km+ resurfaced and 100km surface-treated since April 2020
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads with annual SCANNER data. Generic
                claims will struggle on main routes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — preventative spend just 24.49% in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 656km U-road network on a three-year CVI survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 15% of U-roads in RED — roughly 98km of residential streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C roads in RED up 50% since 2020 (4% → 6%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12,841 pothole fills in five years on a predominantly reactive programme
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Focus on the Specific Evidence</h3>
            <p className="text-brand-200 mb-4">
              The GREEN spend and RED best-practice ratings are background context. An individual assessment
              should focus on the <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, One Trafford reports) — potential evidence of notice, subject to the report details</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class and how the three-year survey cycle applied to the specific location</li>
              <li>• Whether your incident fell in 2020 or 2021 when no U-road condition surveys were published</li>
            </ul>
            <p className="text-white font-medium">
              Mac can search for prior reports, assess photo evidence, and include Trafford&apos;s published
              transparency data as context. You review the material and decide what is relevant to your claim.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Trafford?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated best-practice council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No three-year U-road survey argument</li>
                <li>• No RED best practice citation</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED best practice and 24.5% preventative spend cited</li>
                <li>✅ Three-year U-road survey gap argued</li>
                <li>✅ 12,841 pothole fills in five years documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Trafford</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=trafford"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Trafford Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Trafford spends more than its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on
                aggregate spending. Trafford&apos;s overall rating is AMBER because condition is AMBER and
                best practice is RED: preventative maintenance was just 24.49% of capital spend in 2024/25,
                B and C roads in RED rose from 4% to 6%, and 15% of unclassified roads remain in RED condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Trafford&apos;s RED best practice rating mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT best practice scorecard and Trafford&apos;s published spending figures are contextual
                information about network-level maintenance. They do not establish liability, notice of a
                specific defect, or whether the inspection and repair response for your road was reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 656.195km — 79% of Trafford&apos;s 826km network. The council surveys
                unclassified roads on a three-year cycle across North, Central and South areas because
                &ldquo;the cost &amp; time required to survey the entire network is prohibitive.&rdquo; At the
                2024 survey, 15% of U-roads were in RED condition — roughly 98km of residential streets. If
                your road was not in the surveyed third that year, network-level condition data may not cover it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Trafford filled fewer than 3,000 potholes in 2024/25 — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Repair totals are network-level contextual information and do not determine an individual
                claim. Trafford filled 2,959 potholes in 2024/25 and expects &ldquo;in excess of 2500 pothole
                repairs&rdquo; in 2025/26, while B and C roads in RED rose from 4% in 2020 to 6% in 2024 and
                U-road RED condition has sat at 14–16% since surveys resumed. The records for the specific
                defect remain central.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Were U-road condition surveys conducted during COVID?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Trafford&apos;s transparency report states that no unclassified road surveys were
                conducted during COVID restriction years in 2020 and 2021. For incidents on residential
                roads in those years, there is no published network-level U-road condition data at all — on
                the road class that makes up 79% of the borough&apos;s 826km network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.trafford.gov.uk/sites/default/files/2025-07/Local%20highway%20maintenance%20transparency%20report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Trafford Council Local Highways Maintenance Transparency Report (July 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
